import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { featuresApi } from '../../services/adminApi';

const FeaturesForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: '',
    details: '',
    sort_order: 0,
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isEdit) {
      fetchFeature();
    }
  }, [id]);

  const fetchFeature = async () => {
    try {
      setLoading(true);
      const response = await featuresApi.getById(id);
      if (response.data.success) {
        const feature = response.data.data;
        setFormData({
          title: feature.title || '',
          description: feature.description || '',
          icon: feature.icon || '',
          details: feature.details ? JSON.stringify(feature.details, null, 2) : '',
          sort_order: feature.sort_order || 0,
          is_active: feature.is_active !== undefined ? feature.is_active : true
        });
      } else {
        setError('Özellik bulunamadı');
      }
    } catch (err) {
      console.error('Feature fetch error:', err);
      setError('Özellik yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Details JSON parse et
      let parsedDetails = null;
      if (formData.details.trim()) {
        try {
          parsedDetails = JSON.parse(formData.details);
        } catch (err) {
          setError('Details alanı geçerli JSON formatında olmalıdır');
          setLoading(false);
          return;
        }
      }

      const submitData = {
        ...formData,
        details: parsedDetails,
        sort_order: parseInt(formData.sort_order) || 0
      };

      let response;
      if (isEdit) {
        response = await featuresApi.update(id, submitData);
      } else {
        response = await featuresApi.create(submitData);
      }

      if (response.data.success) {
        alert(isEdit ? 'Özellik başarıyla güncellendi' : 'Özellik başarıyla oluşturuldu');
        navigate('/admin/features');
      } else {
        setError(response.data.message || 'Bir hata oluştu');
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.response?.data?.message || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Styles
  const containerStyle = {
    padding: '1.5rem',
    backgroundColor: '#f9fafb',
    minHeight: '100vh'
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem'
  };

  const titleStyle = {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#111827',
    margin: 0
  };

  const backButtonStyle = {
    color: '#6b7280',
    textDecoration: 'none',
    fontSize: '0.875rem',
    fontWeight: '500',
    padding: '0.5rem 1rem',
    borderRadius: '0.375rem',
    border: '1px solid #d1d5db',
    backgroundColor: 'white',
    cursor: 'pointer',
    transition: 'all 0.2s'
  };

  const errorStyle = {
    marginBottom: '1rem',
    padding: '1rem',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '0.5rem',
    color: '#dc2626',
    fontSize: '0.875rem'
  };

  const formStyle = {
    backgroundColor: 'white',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    borderRadius: '0.5rem',
    padding: '1.5rem',
    border: '1px solid #e5e7eb'
  };

  const fieldStyle = {
    marginBottom: '1.5rem'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '0.5rem'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    color: '#111827',
    backgroundColor: 'white',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    outline: 'none'
  };

  const textareaStyle = {
    ...inputStyle,
    resize: 'vertical',
    minHeight: '6rem'
  };

  const jsonTextareaStyle = {
    ...textareaStyle,
    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
    fontSize: '0.75rem'
  };

  const checkboxContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '1.5rem'
  };

  const checkboxStyle = {
    width: '1rem',
    height: '1rem',
    color: '#3b82f6',
    backgroundColor: 'white',
    border: '1px solid #d1d5db',
    borderRadius: '0.25rem',
    marginRight: '0.5rem',
    cursor: 'pointer'
  };

  const checkboxLabelStyle = {
    fontSize: '0.875rem',
    color: '#111827',
    cursor: 'pointer'
  };

  const helpTextStyle = {
    marginTop: '0.25rem',
    fontSize: '0.75rem',
    color: '#6b7280'
  };

  const buttonContainerStyle = {
    marginTop: '1.5rem',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem'
  };

  const cancelButtonStyle = {
    padding: '0.75rem 1.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#374151',
    backgroundColor: 'white',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  };

  const submitButtonStyle = {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#3b82f6',
    color: 'white',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    opacity: loading ? 0.5 : 1
  };

  const loadingStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '16rem',
    padding: '2rem'
  };

  const spinnerStyle = {
    width: '2rem',
    height: '2rem',
    border: '2px solid #e5e7eb',
    borderTop: '2px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  };

  if (loading && isEdit) {
    return (
      <>
        <Helmet>
          <title>{isEdit ? 'Özellik Düzenle' : 'Yeni Özellik'} - Admin Panel</title>
        </Helmet>
        <div style={containerStyle}>
          <div style={loadingStyle}>
            <div style={spinnerStyle}></div>
            <p style={{ marginLeft: '1rem', color: '#6b7280' }}>Özellik yükleniyor...</p>
          </div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isEdit ? 'Özellik Düzenle' : 'Yeni Özellik'} - Admin Panel</title>
      </Helmet>
      
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h1 style={titleStyle}>
            {isEdit ? 'Özellik Düzenle' : 'Yeni Özellik Ekle'}
          </h1>
          <button
            onClick={() => navigate('/admin/features')}
            style={backButtonStyle}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#f9fafb';
              e.target.style.borderColor = '#9ca3af';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.borderColor = '#d1d5db';
            }}
          >
            ← Geri Dön
          </button>
        </div>

        {error && (
          <div style={errorStyle}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={formStyle}>
          <div style={fieldStyle}>
            <label style={labelStyle}>
              Başlık *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              style={inputStyle}
              placeholder="Özellik başlığı"
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>
              Açıklama *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={4}
              style={textareaStyle}
              placeholder="Özellik açıklaması"
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>
              İkon *
            </label>
            <input
              type="text"
              name="icon"
              value={formData.icon}
              onChange={handleChange}
              required
              style={inputStyle}
              placeholder="Örn: shield-check, zap, users"
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
            />
            <p style={helpTextStyle}>
              Lucide icon ismi (örn: shield-check, zap, users)
            </p>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>
              Detaylar (JSON)
            </label>
            <textarea
              name="details"
              value={formData.details}
              onChange={handleChange}
              rows={6}
              style={jsonTextareaStyle}
              placeholder='["Detay 1", "Detay 2", "Detay 3"]'
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
            />
            <p style={helpTextStyle}>
              JSON formatında ek detaylar (opsiyonel) - Örnek: ["Detay 1", "Detay 2"]
            </p>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>
              Sıralama
            </label>
            <input
              type="number"
              name="sort_order"
              value={formData.sort_order}
              onChange={handleChange}
              min="0"
              style={inputStyle}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <div style={checkboxContainerStyle}>
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              style={checkboxStyle}
            />
            <label style={checkboxLabelStyle}>
              Aktif
            </label>
          </div>

          <div style={buttonContainerStyle}>
            <button
              type="button"
              onClick={() => navigate('/admin/features')}
              style={cancelButtonStyle}
              onMouseOver={(e) => e.target.style.backgroundColor = '#f9fafb'}
              onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={loading}
              style={submitButtonStyle}
              onMouseOver={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = '#2563eb';
                }
              }}
              onMouseOut={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = '#3b82f6';
                }
              }}
            >
              {loading ? 'Kaydediliyor...' : (isEdit ? 'Güncelle' : 'Oluştur')}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default FeaturesForm;